/**
 * Générateur — Automatisme : unités de durée et relations (1 h = 60 min...)
 * Attendu ID : 1033 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève connaît les unités de
 * mesure de durées jour, heure, minute et seconde et les relations qui les
 * lient. »
 * Labels (cartes exerciseur) :
 *   - Les relations entre jour, h, min et s
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * DES FAITS, PAS DES CONVERSIONS : le 1 est toujours d'un côté de l'égalité
 * (« 2 h 30 = 150 min » relève d'un autre objectif). QUATRE relations
 * retenues : 1 jour = 24 h ; 1 h = 60 min ; 1 min = 60 s ; 1 h = 3 600 s —
 * jour↔min et jour↔s ÉCARTÉS (1 440 et 86 400 se recalculent, ne se
 * restituent pas). h↔s SOUS-PONDÉRÉ (~15 % : restitution plus lente,
 * légitime). L'ERREUR CIBLE : le système décimal appliqué au temps
 * (1 h = 100 min). Les formes (validées prof) : « 1 h = ... min », le sens
 * retourné « ... min = 1 h », « 60 min = ... h » (le 1 en réponse) et
 * l'UNITÉ MANQUANTE « 60 min = 1 ... » / « 24 ... = 1 jour » (le format qui
 * discrimine le mieux la vraie connaissance des relations).
 * Domaine minuscule assumé : TIRAGE SANS REMISE par format (cf. 1028-1030).
 * Pas de relier (« 1 h » aurait deux cibles justes : 60 min et 3 600 s).
 */
import {
    randomChoice, shuffle, tirageSansRemise,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1033;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Les relations entre jour, h, min et s';

/** Les QUATRE faits du domaine (fermé). « jour » en toutes lettres,
 *  h/min/s en symboles ; h↔s sous-pondéré. */
const FAITS = [
    { k: 24, kFr: '24', petite: 'h', grande: 'jour', poids: 30 },
    { k: 60, kFr: '60', petite: 'min', grande: 'h', poids: 30 },
    { k: 60, kFr: '60', petite: 's', grande: 'min', poids: 25 },
    { k: 3600, kFr: '3 600', petite: 's', grande: 'h', poids: 15 },
];

const tirerFait = () => {
    const total = FAITS.reduce((s, f) => s + f.poids, 0);
    let r = Math.random() * total;
    for (const f of FAITS) {
        r -= f.poids;
        if (r < 0) return f;
    }
    return FAITS[0];
};

// ─── Format 1 : QCM (nombre, le 1, ou l'unité en réponse) ─────────────────

/** Assemble le payload QCM avec sousType posé dans contenu (routage cdp). */
const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

/** Grilles « nombre en réponse » : l'erreur cible (le système décimal
 *  appliqué au temps) est TOUJOURS là ; 2 tournants parmi 3. */
const GRILLES_NOMBRE = {
    jour: {
        cible: { valeur: '12', code: 'demi_jour' },
        tournants: [
            { valeur: '60', code: 'confusion_heure' },
            { valeur: '100', code: 'systeme_decimal' },
            { valeur: '48', code: 'double' },
        ],
    },
    h_min: {
        cible: { valeur: '100', code: 'systeme_decimal' },
        tournants: [
            { valeur: '24', code: 'confusion_jour' },
            { valeur: '30', code: 'demi' },
            { valeur: '3 600', code: 'confusion_seconde' },
        ],
    },
    min_s: {
        cible: { valeur: '100', code: 'systeme_decimal' },
        tournants: [
            { valeur: '24', code: 'confusion_jour' },
            { valeur: '30', code: 'demi' },
            { valeur: '3 600', code: 'confusion_heure_s' },
        ],
    },
    h_s: {
        cible: { valeur: '360', code: 'zero_manquant' },
        tournants: [
            { valeur: '60', code: 'un_seul_cran' },
            { valeur: '6 000', code: 'zero_de_trop' },
            { valeur: '100', code: 'systeme_decimal' },
        ],
    },
};

const cleGrille = (f) => {
    if (f.grande === 'jour') return 'jour';
    if (f.grande === 'h' && f.petite === 'min') return 'h_min';
    if (f.grande === 'min') return 'min_s';
    return 'h_s';
};

const genQcm = () => {
    const f = tirerFait();
    const r = Math.random();
    if (r < 0.45) {
        // le NOMBRE en réponse, deux sens d'écriture
        const g = GRILLES_NOMBRE[cleGrille(f)];
        const dis = [g.cible, ...shuffle(g.tournants).slice(0, 2)];
        const texte = Math.random() < 0.5
            ? `<p><b>Complète : 1 ${f.grande} = ... ${f.petite}</b></p>`
            : `<p><b>Complète : ... ${f.petite} = 1 ${f.grande}</b></p>`;
        return finirQcm(f.kFr, dis, texte, 'nombre');
    }
    if (r < 0.70) {
        // le 1 en réponse : « 60 min = ... h »
        const dis = [
            { valeur: f.kFr, code: 'recopie_nombre' },
            ...shuffle([
                { valeur: '2', code: 'petit_nombre' },
                { valeur: '10', code: 'systeme_decimal' },
                { valeur: '12', code: 'demi_de_24' },
            ]).slice(0, 2),
        ];
        return finirQcm('1', dis, `<p><b>Complète : ${f.kFr} ${f.petite} = ... ${f.grande}</b></p>`, 'un');
    }
    // l'UNITÉ en réponse : « 60 min = 1 ... » — les 4 unités en grille
    const dis = shuffle(['jour', 'h', 'min', 's'].filter((u) => u !== f.grande))
        .map((u) => ({ valeur: u, code: u === f.petite ? 'recopie_unite' : 'autre_unite' }));
    return finirQcm(f.grande, dis, `<p><b>Complète : ${f.kFr} ${f.petite} = 1 ...</b></p>`, 'unite');
};

// ─── Format 2 : trous saisie (égalités, le 1, ou l'unité manquante) ───────

/** Une ligne d'égalité à un trou — 5 formes (prof 2026-08-22). */
const ligneEgalite = (f, forme) => {
    switch (forme) {
        case 'nombre_droite': return `1 ${f.grande} = {{${f.kFr}}} ${f.petite}`;
        case 'nombre_gauche': return `{{${f.kFr}}} ${f.petite} = 1 ${f.grande}`;
        case 'le_un': return `${f.kFr} ${f.petite} = {{1}} ${f.grande}`;
        case 'unite_gauche': return `${f.kFr} {{${f.petite}}} = 1 ${f.grande}`;
        default: return `${f.kFr} ${f.petite} = 1 {{${f.grande}}}`;
    }
};

const genTrouSaisie = () => {
    const variante = randomChoice(['egalites', 'egalites', 'unites']);
    const formes = variante === 'egalites'
        ? ['nombre_droite', 'nombre_gauche', 'le_un']
        : ['unite_gauche', 'unite_droite'];
    // 1 ou 2 lignes, FAITS distincts
    const nb = randomChoice([1, 2, 2]);
    const faits = shuffle([...FAITS]).slice(0, nb);
    const lignes = faits.map((f) => ligneEgalite(f, randomChoice(formes)));
    return makeTexteTrou(
        lignes.join('\n'),
        variante === 'egalites'
            ? '<p><b>Complète les égalités.</b></p>'
            : '<p><b>Complète avec l\'unité qui convient (jour, h, min ou s).</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : V/F (pool fermé étiqueté à la main) ───────────────────────

const AFFIRMATIONS = [
    { t: '1 jour = 24 h', v: true },
    { t: '1 h = 60 min', v: true },
    { t: '1 min = 60 s', v: true },
    { t: '1 h = 3 600 s', v: true },
    { t: '24 h = 1 jour', v: true },
    { t: '60 s = 1 min', v: true },
    { t: '1 h = 100 min', v: false },                   // L'ERREUR CIBLE
    { t: '1 jour = 12 h', v: false },
    { t: '1 h = 360 s', v: false },
    { t: '1 min = 100 s', v: false },
    { t: '1 jour = 60 h', v: false },
    { t: '60 min = 1 jour', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[1].t, AFFIRMATIONS[6].t, AFFIRMATIONS[0].t, AFFIRMATIONS[7].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'DES FAITS, PAS DES CONVERSIONS : le 1 toujours d\'un côté de l\'égalité',
        'QUATRE relations : 1 jour = 24 h ; 1 h = 60 min ; 1 min = 60 s ; 1 h = 3 600 s (jour↔min et jour↔s écartés)',
        'h↔s sous-pondéré (~15 %) : restitution plus lente, légitime',
        'domaine fermé assumé : tirage sans remise par format',
    ],
    durees_qcm_unique: {
        support: 'l\'égalité sur les 4 relations — trois sous-types : nombre / le 1 / l\'unité',
        nombre: {
            cible: 'le système décimal appliqué au temps (100), ou 12 (demi-jour), ou 360 (zéro manquant pour h↔s) — toujours présente',
            tournants: '2 parmi 3 par relation (confusions d\'unité, demi, zéros)',
        },
        un: { pool: 'recopie du nombre (60/24/3 600) toujours + 2 parmi {2 ; 10 ; 12}' },
        unite: { pool: 'les 3 autres unités (recopie de l\'unité de gauche incluse)' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    durees_qcm_unique: genQcm,
    durees_texte_trou_saisie: genTrouSaisie,
    durees_tableau_vf: genVraiFaux,
};

const FORMAT_LABELS = {
    durees_qcm_unique: 'QCM',
    durees_texte_trou_saisie: 'Saisie',
    durees_tableau_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'unitesdurees',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // domaine minuscule (4 faits) : TIRAGE SANS REMISE par format —
    // signature = la QUESTION + le sousType
    const payload = tirageSansRemise(
        `gen_1033:${key}`,
        () => GENERATORS[key](),
        (p) => p.texte
            + JSON.stringify(p.contenu.phrase
                || (p.elements_additionnels?.elements?.[0]?.config?.lignes ?? ''))
            + JSON.stringify(p._meta.params?.sousType ?? ''),
        250,
    );
    payload._meta.generatorKey = key;
    return payload;
};
